import { useMemo, useState } from "react"
import { Box, Checkbox, HStack, NativeSelect, Popover, Portal, Stack, Table } from "@chakra-ui/react"
import { useQueryClient } from "@tanstack/react-query"
import { Button, Input, PaginationControls, Text, toaster } from "@/components"
import { usePagination } from "@/hooks"
import { createRumor } from "@/api"
import { CompanyRumor } from "@/types"

interface Props {
  companyId: number
  rumors: CompanyRumor[]
}

const ITEMS_PER_PAGE = 50

export const CompanyRumors = ({ companyId, rumors }: Props) => {
  const queryClient = useQueryClient()

  const allFlags = useMemo(() => {
    const flags = new Set(rumors.map((r) => r.companyRumorFlag?.name || "No Flag"))
    return Array.from(flags).sort()
  }, [rumors])

  const [selectedFlags, setSelectedFlags] = useState<Set<string>>(() => {
    return new Set(rumors.map((r) => r.companyRumorFlag?.name || "No Flag"))
  })
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rumorDate, setRumorDate] = useState("")
  const [rumorFlagId, setRumorFlagId] = useState("")
  const [rumorComment, setRumorComment] = useState("")

  const resetForm = () => {
    setRumorDate("")
    setRumorFlagId("")
    setRumorComment("")
    setShowForm(false)
  }

  const handleCreateRumor = async () => {
    setIsSubmitting(true)
    try {
      await createRumor(companyId, {
        rumorFlagId: rumorFlagId ? Number(rumorFlagId) : null,
        comment: rumorComment || null,
        date: rumorDate || null,
      })
      toaster.create({ title: "Rumor created", type: "success" })
      queryClient.invalidateQueries({ queryKey: ["companyInfo", companyId] })
      resetForm()
    } catch (error) {
      console.error("Error creating rumor:", error)
      toaster.create({ title: "Failed to create rumor", type: "error" })
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return "N/A"
    return new Date(dateString).toLocaleDateString()
  }

  const handleFlagToggle = (flag: string) => {
    const newSelected = new Set(selectedFlags)
    if (newSelected.has(flag)) {
      newSelected.delete(flag)
    } else {
      newSelected.add(flag)
    }
    setSelectedFlags(newSelected)
  }

  const handleSelectAll = () => {
    setSelectedFlags(new Set(allFlags))
  }

  const handleClearAll = () => {
    setSelectedFlags(new Set())
  }

  const getFlagName = (rumor: CompanyRumor) => {
    return rumor.companyRumorFlag?.name || "No Flag"
  }

  const filteredRumors = useMemo(() => {
    return rumors.filter((rumor) => {
      const flagName = getFlagName(rumor)
      return selectedFlags.has(flagName)
    })
  }, [rumors, selectedFlags])

  const sortedRumors = useMemo(() => {
    return [...filteredRumors].sort(
      (a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()
    )
  }, [filteredRumors])

  const getRowBackground = (rumor: CompanyRumor) => {
    if (rumor.rumorFlagId === 4) return "row.green"
    if ([2, 3, 6, 7].includes(rumor.rumorFlagId)) return "row.red"
    if (rumor.rumorFlagId === 1 || (rumor.comment && rumor.comment.toLowerCase().includes("revenue"))) return "row.blue"
    return "table.row"
  }

  const getRowBorderColor = (flagId: number) => {
    if (flagId === 4) return "border.green"
    if ([2, 3, 6, 7].includes(flagId)) return "border.red"
    return undefined
  }

  const { paginatedData: paginatedRumors, currentPage, totalPages, startIndex, endIndex, setPage, getPageNumbers } = usePagination(sortedRumors, ITEMS_PER_PAGE)

  return (
    <Box w="100%" p={5} border="1px" borderColor="gray.200" borderRadius={10} boxShadow="sm" bg="card.bg">
      <HStack mb={4} justifyContent="space-between">
        <Text fontSize="lg" fontWeight="bold">
          Rumors ({filteredRumors.length} of {rumors.length})
        </Text>

        <HStack gap={2}>
          {!showForm && (
            <Button size="sm" onClick={() => setShowForm(true)}>
              + Add Rumor
            </Button>
          )}
          <Popover.Root>
            <Popover.Trigger asChild>
              <Button size="sm" variant="outline">
                Filter by Flag ({selectedFlags.size}/{allFlags.length})
              </Button>
            </Popover.Trigger>
            <Portal>
              <Popover.Positioner>
                <Popover.Content w="250px">
                  <Popover.Arrow />
                  <Popover.Body>
                    <HStack mb={2} gap={2}>
                      <Button size="xs" variant="ghost" onClick={handleSelectAll}>
                        Select All
                      </Button>
                      <Button size="xs" variant="ghost" onClick={handleClearAll}>
                        Clear All
                      </Button>
                    </HStack>
                    <Stack maxH="300px" overflowY="auto" gap={1}>
                      {allFlags.map((flag) => (
                        <Checkbox.Root
                          key={flag}
                          checked={selectedFlags.has(flag)}
                          onCheckedChange={() => handleFlagToggle(flag)}
                          size="sm"
                        >
                          <Checkbox.HiddenInput />
                          <Checkbox.Control />
                          <Checkbox.Label>{flag}</Checkbox.Label>
                        </Checkbox.Root>
                      ))}
                    </Stack>
                  </Popover.Body>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </HStack>
      </HStack>

      {showForm && (
        <Box mb={4} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="bg.subtle">
          <Text fontSize="sm" fontWeight="bold" mb={3}>New Rumor</Text>
          <HStack gap={3} mb={3} flexWrap="wrap">
            <Stack gap={1} flex="1" minW="140px">
              <Text fontSize="xs" color="text.secondary">Date</Text>
              <Input type="date" size="sm" value={rumorDate} onChange={(e) => setRumorDate(e.target.value)} />
            </Stack>
            <Stack gap={1} flex="1" minW="160px">
              <Text fontSize="xs" color="text.secondary">Flag</Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field value={rumorFlagId} onChange={(e) => setRumorFlagId(e.target.value)}>
                  <option value="">No Flag</option>
                  <option value="1">Revenue</option>
                  <option value="2">Negative</option>
                  <option value="3">Executive Departure</option>
                  <option value="4">Flag Mitigated</option>
                  <option value="5">No Flag</option>
                  <option value="6">No Founders Left</option>
                  <option value="7">Penalty Box</option>
                  <option value="8">Company has exited</option>
                  <option value="9">MEMO</option>
                </NativeSelect.Field>
              </NativeSelect.Root>
            </Stack>
          </HStack>
          <Stack gap={1} mb={3}>
            <Text fontSize="xs" color="text.secondary">Comment</Text>
            <Input size="sm" placeholder="Rumor details..." value={rumorComment} onChange={(e) => setRumorComment(e.target.value)} />
          </Stack>
          <HStack gap={2}>
            <Button size="sm" onClick={handleCreateRumor} loading={isSubmitting}>
              Create Rumor
            </Button>
            <Button size="sm" variant="outline" onClick={resetForm}>
              Cancel
            </Button>
          </HStack>
        </Box>
      )}

      {sortedRumors.length === 0 ? (
        <Text color="text.muted">No rumors found for the selected filters.</Text>
      ) : (
        <>
          <Table.Root size="sm" striped css={{ "& tr:nth-of-type(odd) td": { bg: "table.stripe" } }}>
            <Table.Header>
              <Table.Row bg="table.row">
                <Table.ColumnHeader>Date</Table.ColumnHeader>
                <Table.ColumnHeader>Flag</Table.ColumnHeader>
                <Table.ColumnHeader>Comment</Table.ColumnHeader>
                <Table.ColumnHeader>Created By</Table.ColumnHeader>
                <Table.ColumnHeader>Created Date</Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {paginatedRumors.map((rumor) => (
                <Table.Row
                  key={rumor.id}
                  bg={getRowBackground(rumor)}
                  borderLeft={getRowBorderColor(rumor.rumorFlagId) ? "3px solid" : undefined}
                  borderLeftColor={getRowBorderColor(rumor.rumorFlagId)}
                >
                  <Table.Cell>{formatDate(rumor.date)}</Table.Cell>
                  <Table.Cell>{getFlagName(rumor)}</Table.Cell>
                  <Table.Cell>{rumor.comment}</Table.Cell>
                  <Table.Cell>{rumor.createdBy}</Table.Cell>
                  <Table.Cell>{formatDate(rumor.createdAt)}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={sortedRumors.length}
            startIndex={startIndex}
            endIndex={endIndex}
            onPageChange={setPage}
            getPageNumbers={getPageNumbers}
          />
        </>
      )}
    </Box>
  )
}
